import React, { useState, useEffect } from 'react';
import NavigationBar from '../../components/ui/NavigationBar';
import UserStatusIndicator from '../../components/ui/UserStatusIndicator';
import CallStatusWidget from '../../components/ui/CallStatusWidget';
import Icon from '../../components/AppIcon';
import SipSettingsTab from './components/SipSettingsTab';
import UserManagementTab from './components/UserManagementTab';
import CallRoutingTab from './components/CallRoutingTab';
import IntegrationsTab from './components/IntegrationsTab';
import SecurityTab from './components/SecurityTab';

const SystemSettings = () => {
  const [activeTab, setActiveTab] = useState('sip');
  const [currentLanguage, setCurrentLanguage] = useState('tr');

  useEffect(() => {
    // Check localStorage for saved language preference
    const savedLanguage = localStorage.getItem('language') || 'tr';
    setCurrentLanguage(savedLanguage);
  }, []);

  const tabs = [
    {
      id: 'sip',
      label: 'SIP Ayarları',
      icon: 'Phone',
      description: 'SIP protokol ve ses ayarları'
    },
    {
      id: 'users',
      label: 'Kullanıcı Yönetimi',
      icon: 'Users',
      description: 'Temsilci hesapları ve yetkiler'
    },
    {
      id: 'routing',
      label: 'Arama Yönlendirme',
      icon: 'Route',
      description: 'Kuyruk ve yönlendirme kuralları'
    },
    {
      id: 'integrations',
      label: 'Entegrasyonlar',
      icon: 'Plug',
      description: 'Harici sistem bağlantıları'
    },
    {
      id: 'security',
      label: 'Güvenlik',
      icon: 'Shield',
      description: 'Güvenlik politikaları ve denetim'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'sip':
        return <SipSettingsTab />;
      case 'users':
        return <UserManagementTab />;
      case 'routing':
        return <CallRoutingTab />;
      case 'integrations':
        return <IntegrationsTab />;
      case 'security':
        return <SecurityTab />;
      default:
        return <SipSettingsTab />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      <UserStatusIndicator />
      <CallStatusWidget />
      <div className="content-offset">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Settings" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Sistem Ayarları</h1>
                <p className="text-slate-600">Çağrı merkezi sistem yapılandırması ve yönetimi</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Settings Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <h3 className="font-medium text-slate-900 mb-4">Ayar Kategorileri</h3>
                <nav className="space-y-2">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`w-full text-left p-3 rounded-lg transition-all duration-150 ${
                        activeTab === tab?.id
                          ? 'bg-primary/10 text-primary border border-primary/20' :'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon name={tab?.icon} size={18} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{tab?.label}</div>
                          <div className="text-xs text-slate-500 mt-1">{tab?.description}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </nav>

                {/* System Status */}
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h4 className="font-medium text-slate-900 mb-3">Sistem Durumu</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">SIP Sunucusu</span>
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-success rounded-full"></div>
                        <span className="text-success font-medium">Bağlı</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Veritabanı</span>
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-success rounded-full"></div>
                        <span className="text-success font-medium">Aktif</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Kayıt Servisi</span>
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-warning rounded-full"></div>
                        <span className="text-warning font-medium">Uyarı</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Yedekleme</span>
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-success rounded-full"></div>
                        <span className="text-success font-medium">Güncel</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h4 className="font-medium text-slate-900 mb-3">Hızlı İşlemler</h4>
                  <div className="space-y-2">
                    <button className="w-full text-left p-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded flex items-center space-x-2">
                      <Icon name="Download" size={14} />
                      <span>Ayarları Dışa Aktar</span>
                    </button>
                    <button className="w-full text-left p-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded flex items-center space-x-2">
                      <Icon name="Upload" size={14} />
                      <span>Ayarları İçe Aktar</span>
                    </button>
                    <button className="w-full text-left p-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded flex items-center space-x-2">
                      <Icon name="RefreshCw" size={14} />
                      <span>Sistemi Yeniden Başlat</span>
                    </button>
                    <button className="w-full text-left p-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded flex items-center space-x-2">
                      <Icon name="HardDrive" size={14} />
                      <span>Yedek Oluştur</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Settings Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-lg border border-slate-200 min-h-[600px]">
                {/* Tab Header */}
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-center space-x-3">
                    <Icon name={tabs?.find(tab => tab?.id === activeTab)?.icon || 'Settings'} size={20} className="text-primary" />
                    <div>
                      <h2 className="text-xl font-semibold text-slate-900">
                        {tabs?.find(tab => tab?.id === activeTab)?.label}
                      </h2>
                      <p className="text-sm text-slate-600 mt-1">
                        {tabs?.find(tab => tab?.id === activeTab)?.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {renderTabContent()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;